import React, { ReactNode } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { Theme } from '@allenai/varnish';

import { Properties, UiWidgets } from '../data';
import {
    UiSchemaKey,
    defaultDateTimeDisplay,
    Indexable,
    WidgetUiSchemaKey,
    displayDate,
} from '../utils/util';
import { Markdown } from './Markdown';
import { ArrowOut } from '../icons/ArrowOut';

const ExternalLink = ({ url, label }: { url: string; label: ReactNode }) => (
    <a href={url} target="_blank" rel="noopener noreferrer">
        <ArrowOut color={Theme.palette.primary.default.hex} /> {label}
    </a>
);
// ui specific work to display types of data in a specific way
// tables and details lists act slightly differently
export const columnRender = (
    properties: Properties,
    widgetKey?: WidgetUiSchemaKey
): ((val: any, record?: any) => JSX.Element) => {
    // fall back to base Widget
    let widget = properties[UiSchemaKey.Widget];
    let widgetProps = properties[UiSchemaKey.WidgetProps];
    switch (widgetKey) {
        case UiSchemaKey.WidgetDetail: {
            widget = properties[UiSchemaKey.WidgetDetail] || widget;
            widgetProps = properties[UiSchemaKey.WidgetDetailProps] || widgetProps;
            break;
        }
        case UiSchemaKey.WidgetTable: {
            widget = properties[UiSchemaKey.WidgetTable] || widget;
            widgetProps = properties[UiSchemaKey.WidgetTableProps] || widgetProps;
            break;
        }
    }
    switch (widget) {
        case UiWidgets.Bold:
            return (val: string | undefined) => <Bold>{val}</Bold>;
        case UiWidgets.Date:
            return (val: string | undefined) => {
                if (!val) {
                    return <></>;
                }
                return <>{displayDate(val, widgetProps?.format, widgetProps?.displayFormat)}</>;
            };
        case UiWidgets.DateTime:
            return (val: string | undefined) => {
                const diff = dayjs().diff(dayjs(val), 's');
                return diff < 60 ? (
                    <>{`${diff} seconds ago`}</>
                ) : (
                    <>{dayjs(val).format(defaultDateTimeDisplay)}</>
                );
            };
        case UiWidgets.Link:
            return (val: string | undefined, record?: Indexable<any>) => {
                const label =
                    widgetProps?.label || (record && record[widgetProps?.labelSrc]) || val;
                const url = (record && record[widgetProps?.urlSrc]) || val;
                return url ? <ExternalLink url={url} label={label} /> : <></>;
            };
        case UiWidgets.LinkList:
            return (val: string[] | undefined, record?: Indexable<any>) => {
                if (!val) {
                    return <></>;
                }
                return (
                    <span>
                        {val.map((url, k) => {
                            const label =
                                widgetProps?.label ||
                                (record && record[widgetProps?.labelSrc]) ||
                                url;
                            const formattedLabel = `${label}${k < val.length - 1 ? ', ' : ''}`;

                            // only show the little external icon for the first one, seeing it over and over again looks bad
                            return k === 0 ? (
                                <ExternalLink url={url} label={formattedLabel} />
                            ) : (
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                    {formattedLabel}
                                </a>
                            );
                        })}
                    </span>
                );
            };
        case UiWidgets.Markdown:
            return (val: string | undefined) => {
                if (!val) {
                    return <></>;
                }
                return <Markdown>{val}</Markdown>;
            };
        case UiWidgets.Omit:
            return (_val: string | undefined) => <></>;
        case UiWidgets.ShowMore:
            return (val: string | undefined) => (
                <Typography.Paragraph
                    ellipsis={{
                        rows: Number(widgetProps?.rows) ?? 6,
                        expandable: true,
                        symbol: 'more',
                    }}>
                    {val}
                </Typography.Paragraph>
            );
        case UiWidgets.Tags:
            return (tags: string[] | undefined) => {
                if (!tags) {
                    return <></>;
                }
                return (
                    <span>
                        {tags.map((tag) => (
                            <Tag key={tag} color="purple">
                                {tag}
                            </Tag>
                        ))}
                    </span>
                );
            };
        case UiWidgets.TitleLink:
            return (val: any | undefined, record?: Indexable<any>) => {
                const title =
                    widgetProps?.label || (record && record[widgetProps?.labelSrc]) || val;

                const getURL = () => {
                    // with custom url source prefix
                    // can be used for things like https://allenai.org/data/ + dataset short name
                    if (widgetProps?.urlSrcPrefix) {
                        const postfix = (record && record[widgetProps?.urlSrc]) || val;
                        return `${widgetProps.urlSrcPrefix}${postfix}`;
                    }
                    return `${(record && record[widgetProps?.urlSrc]) || ''}`;
                };

                const url = getURL();
                return url ? (
                    <>
                        <ExternalLink url={url} label={<Bold>{title}</Bold>} />
                    </>
                ) : (
                    <>
                        <Bold>{title}</Bold>
                    </>
                );
            };
        case UiWidgets.Truncated:
            return (val: string | undefined) => {
                if (!val) {
                    return <></>;
                }
                const shortened = val.substring(0, Number(widgetProps?.maxLength) ?? 100);
                return shortened.length < val.length ? (
                    <Tooltip title={val.substring(0, 1000)}>
                        <Break>{shortened}</Break>
                        {' ...'}
                    </Tooltip>
                ) : (
                    <>{val}</>
                );
            };
        case UiWidgets.YesNo:
            return (val: boolean | undefined) => (val ? <>Yes</> : <>No</>);
        default:
            return (val: any | undefined) => <>{val}</>;
    }
};

const Break = styled.span`
    word-break: break-word;
`;

const Bold = styled.span`
    font-weight: bold;
`;
